import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import MainLayout from './components/layout/MainLayout';
import ErrorBoundary from './components/design-system/ErrorBoundary';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import ContractReview from './pages/ContractReview';
import ContractsList from './pages/ContractsList';
import ContractCreate from './pages/ContractCreate';
import ContractUpdate from './pages/ContractUpdate';
import ShipmentsList from './pages/ShipmentsList';
import FinancialOverview from './pages/FinancialOverview';
import ContainerTracking from './pages/ContainerTracking';
import MasterTrackingSheet from './pages/MasterTrackingSheet';
import CompaniesList from './pages/CompaniesList';
import ForwardersList from './pages/ForwardersList';
import DocumentRequirements from './pages/DocumentRequirements';
import CarriersList from './pages/CarriersList';
import GoodsReview from './pages/GoodsReview';
import TemplatesList from './pages/TemplatesList';
import TemplateEditor from './pages/TemplateEditor';
import CompanyBrandingPage from './pages/settings/company-branding';
import DocumentSnippetsPage from './pages/settings/document-snippets';
import FinancialDefaultsPage from './pages/settings/financial-defaults';

import PendingUpdatesReview from './pages/PendingUpdatesReview';
import PendingInvoicesList from './pages/PendingInvoicesList';
import PendingInvoiceEditor from './pages/PendingInvoiceEditor';
import InvoiceReconciler from './pages/InvoiceReconciler';
import WireEntry from './pages/WireEntry';
import CheckEntry from './pages/CheckEntry';
import PaymentsList from './pages/PaymentsList';
import CommissionsDue from './pages/CommissionsDue';
import RevenueReport from './pages/RevenueReport';
import OutstandingBySupplier from './pages/reports/OutstandingBySupplier';
import ShipmentsByStatus from './pages/reports/ShipmentsByStatus';
import ShinyeiReconciliation from './pages/reports/ShinyeiReconciliation';
import AuthTest from './pages/AuthTest';
import SimpleAuthTest from './pages/SimpleAuthTest';
import './App.css';

function App() {
  const handleGlobalError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Log to error tracking service in production
    console.error('Global error caught:', error, errorInfo);
    
    // TODO: Report to error tracking service
    // errorTrackingService.report(error, errorInfo);
  };

  return (
    <ErrorBoundary 
      onError={handleGlobalError}
      title="Atlas Application Error"
      message="The application encountered an unexpected error. Please try refreshing the page."
    >
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          {/* Temporary: Testing routes without auth */}
          <Route path="/contracts-test" element={<ContractsList />} />
          <Route path="/auth-test" element={<AuthTest />} />
          <Route path="/simple-auth-test" element={<SimpleAuthTest />} />
          
          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="contracts" element={<ContractsList />} />
            <Route path="contracts/create" element={<ContractCreate />} />
            <Route path="contracts/:reference" element={<ContractReview />} />
            <Route path="contracts/:reference/edit" element={<ContractUpdate />} />
            <Route path="shipments" element={<ShipmentsList />} />
            <Route path="financial" element={<FinancialOverview />} />
            <Route path="tracking" element={<ContainerTracking />} />
            <Route path="reports/master-tracking" element={<MasterTrackingSheet />} />
            <Route path="contacts" element={<CompaniesList />} />
            <Route path="forwarders" element={<ForwardersList />} />
            <Route path="document-requirements" element={<DocumentRequirements />} />
            <Route path="carriers" element={<CarriersList />} />
            <Route path="goods-review" element={<GoodsReview />} />
            <Route path="reconciliation" element={<Navigate to="/payments" replace />} />
            <Route path="pending-updates" element={<PendingUpdatesReview />} />
            <Route path="pending-invoices" element={<PendingInvoicesList />} />
            <Route path="pending-invoices/:id" element={<PendingInvoiceEditor />} />
            <Route path="pending-invoices/:id/reconcile" element={<InvoiceReconciler />} />
            <Route path="wire-entry" element={<WireEntry />} />
            <Route path="check-entry" element={<CheckEntry />} />
            <Route path="payments" element={<PaymentsList />} />
            <Route path="commissions-due" element={<CommissionsDue />} />
            <Route path="reports/revenue" element={<RevenueReport />} />
            <Route path="reports/outstanding-by-supplier" element={<OutstandingBySupplier />} />
            <Route path="reports/shipments-by-status" element={<ShipmentsByStatus />} />
            <Route path="reports/shinyei-reconciliation" element={<ShinyeiReconciliation />} />
            <Route path="settings/templates" element={<TemplatesList />} />
            <Route path="settings/templates/new" element={<TemplateEditor />} />
            <Route path="settings/templates/:id" element={<TemplateEditor />} />
            <Route path="settings/company" element={<CompanyBrandingPage />} />
            <Route path="settings/snippets" element={<DocumentSnippetsPage />} />
            <Route path="settings/defaults" element={<FinancialDefaultsPage />} />
          </Route>

          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;